var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useCallback, useContext, useEffect, useState } from "react";
import { isValidElementType } from 'react-is';
import { hasValue, loadAllScriptsByModuleId, lookupComponent, getModuleIdFromWidget, isUserAllowed, normalizeComponentId } from "@utils";
import { NotAuthorized } from "@core/layout/components/ErrorPages";
import { UXPContext } from "@core/context/Context";
import { LoadingSpinner, DefaultLoader } from "@widget/loaders";
import { shouldShowPlaceholder, hasConfigOptions, getMergedWidgetProps } from "@core/dashboard/dashboardUtils";
import { useAddOns } from "@helper/AddOn/useAddOns";
import InstallAddOn from "@helper/AddOn/InstallAddOn";
import ErrorBoundary from "@helper/ErrorBoundary/ErrorBoundary";
import ViewNotFound, { describeModule } from "@helper/ComponentRenderer/ViewNotFound";
import ButtonComponent from '@widget/buttons/ButtonComponent';
/** How long a known bundle may take to register its component before "not found" is shown. */
export const COMPONENT_RESOLVE_TIMEOUT_MS = 15000;
const ConfigPlaceholder = ({ name, message, onConfigure }) => {
    const context = useContext(UXPContext);
    return (_jsxs("div", { className: "uxpcore_componentrenderer__config-placeholder", children: [_jsx("p", { className: "uxpcore_componentrenderer__config-placeholder-name", children: name }), _jsx("p", { className: "uxpcore_componentrenderer__config-placeholder-message", children: message || context.$L('uxp-core.text.need-to-configure-widget') }), onConfigure && (_jsx(ButtonComponent, { title: context.$L('uxp-core.text.configure'), onClick: onConfigure, leftIcon: "fas cog" }))] }));
};
function extractDefaultPropsFromSchema(instance) {
    var _a;
    return (((_a = instance.configs) === null || _a === void 0 ? void 0 : _a.props) || [])
        .filter((p) => 'name' in p && p.value !== undefined)
        .reduce((acc, p) => {
        acc[p.name] = p.value;
        return acc;
    }, {});
}
const ComponentRendererBase = ({ moduleId, componentId, componentInstance, configuredProps, hasConfigured, onConfigureRequest, configNeededMessage, additionalProps, notFoundMessage, loader, isPreview = false, onAddonInstalled, forceConfigView = false, }) => {
    var _a, _b, _c, _d, _e, _f;
    const context = useContext(UXPContext);
    const addOns = useAddOns();
    const [resolvedInstance, setResolvedInstance] = useState(componentInstance !== null && componentInstance !== void 0 ? componentInstance : null);
    // Path A starts in loading so the first commit is never the not-found branch.
    const [loading, setLoading] = useState(componentInstance == null);
    const [installInstance, setInstallInstance] = useState(null);
    const [diagnostic, setDiagnostic] = useState(null);
    // A known bundle may still be executing when the lookup misses (its script tag is in the
    // DOM, so the load resolves early). Keep the loader up until it registers or this expires.
    const [waitedOut, setWaitedOut] = useState(false);
    useEffect(() => {
        setWaitedOut(false);
        const t = setTimeout(() => setWaitedOut(true), COMPONENT_RESOLVE_TIMEOUT_MS);
        return () => clearTimeout(t);
    }, [moduleId, componentId]);
    // Path A: resolve component by moduleId/componentId with lazy script loading
    const loadComponent = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const id = `${moduleId !== null && moduleId !== void 0 ? moduleId : ''}/${componentId !== null && componentId !== void 0 ? componentId : ''}`.toLowerCase();
        if (!hasValue(moduleId) || !hasValue(componentId)) {
            setDiagnostic(Object.assign({ cause: 'no-ids', id }, describeModule(context, moduleId)));
            setLoading(false);
            return;
        }
        setLoading(true);
        setDiagnostic(null);
        let reg = lookupComponent(id);
        if (!reg) {
            // Captured before the load: an unregistered bundle resolves silently
            // (loadAllScriptsByModuleId returns early), so it is indistinguishable after.
            const hasBundle = ((_c = (_b = (_a = context === null || context === void 0 ? void 0 : context.scriptFiles) === null || _a === void 0 ? void 0 : _a[moduleId]) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0;
            try {
                yield loadAllScriptsByModuleId(context, moduleId);
            }
            catch (_e) {
                setDiagnostic(Object.assign({ cause: 'load-failed', id }, describeModule(context, moduleId)));
                setResolvedInstance(null);
                setLoading(false);
                return;
            }
            reg = lookupComponent(id);
            if (!reg) {
                console.error('ComponentRenderer: component not found after loading script', moduleId, componentId);
                setDiagnostic(Object.assign({ cause: hasBundle ? 'not-registered' : 'bundle-missing', id }, describeModule(context, moduleId)));
                setResolvedInstance(null);
                setLoading(false);
                return;
            }
        }
        const schemaDefaults = extractDefaultPropsFromSchema({ configs: reg.configs });
        const instance = {
            _id: id,
            id: reg.id,
            key: id,
            name: reg.name || componentId,
            component: reg.component,
            configs: reg.configs,
            defaultProps: (_d = reg.defaultProps) !== null && _d !== void 0 ? _d : schemaDefaults,
            configuredProps: configuredProps !== null && configuredProps !== void 0 ? configuredProps : {},
            hasConfigured: hasConfigured !== null && hasConfigured !== void 0 ? hasConfigured : Object.keys(configuredProps || {}).length > 0,
            installed: reg.installed,
            usecaseId: reg.usecaseId,
            usecaseName: reg.usecaseName,
        };
        setResolvedInstance(instance);
        setLoading(false);
    }), [moduleId, componentId, context, configuredProps, hasConfigured]);
    useEffect(() => {
        if (componentInstance == null) {
            loadComponent();
            return;
        }
        // Path B: the instance is pre-resolved by the caller (e.g. a dashboard tile),
        // but on a cold page load the widget's module bundle may not be loaded yet
        // (bundles are idle-deferred), so `component` is null. Self-heal like Path A:
        // load the module and re-resolve instead of rendering "invalid component type".
        if (isValidElementType(componentInstance.component)) {
            setResolvedInstance(componentInstance);
            return;
        }
        let cancelled = false;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            setDiagnostic(null);
            const moduleId = getModuleIdFromWidget(componentInstance);
            let reg = lookupComponent(componentInstance.id);
            if (!reg && moduleId) {
                try {
                    yield loadAllScriptsByModuleId(context, moduleId);
                }
                catch (_a) {
                    if (cancelled)
                        return;
                    setDiagnostic(Object.assign({ cause: 'load-failed', id: componentInstance.id }, describeModule(context, moduleId)));
                    setResolvedInstance(null);
                    setLoading(false);
                    return;
                }
                reg = lookupComponent(componentInstance.id);
            }
            if (cancelled)
                return;
            // Merge the registry hit onto the instance, preserving its configured state.
            // Fall back to the instance as-is if still unresolved (renders "not found").
            setResolvedInstance(reg ? Object.assign(Object.assign({}, componentInstance), { component: reg.component, configs: reg.configs }) : componentInstance);
            setLoading(false);
        }))();
        return () => { cancelled = true; };
    }, [componentInstance, loadComponent, context]);
    // A bundle that is still executing registers after our lookup, which would otherwise
    // leave a permanent (and false) "not registered". Re-resolve when it arrives.
    useEffect(() => {
        if (componentInstance != null || !diagnostic)
            return;
        if (diagnostic.cause !== 'not-registered' && diagnostic.cause !== 'bundle-missing')
            return;
        const wanted = normalizeComponentId(diagnostic.id);
        const handler = (e) => {
            const detail = e.detail;
            if (normalizeComponentId((detail === null || detail === void 0 ? void 0 : detail.normalizedId) || (detail === null || detail === void 0 ? void 0 : detail.id) || '') !== wanted)
                return;
            if (lookupComponent(diagnostic.id))
                loadComponent();
        };
        window.addEventListener('uxp:component-registered', handler);
        return () => window.removeEventListener('uxp:component-registered', handler);
    }, [diagnostic, componentInstance, loadComponent]);
    if (loading || ((diagnostic === null || diagnostic === void 0 ? void 0 : diagnostic.cause) === 'not-registered' && !waitedOut)) {
        return _jsx("div", { className: "uxpcore_componentrenderer__container", children: loader !== null && loader !== void 0 ? loader : _jsx(LoadingSpinner, {}) });
    }
    if (!resolvedInstance) {
        return (_jsx("div", { className: "uxpcore_componentrenderer__container uxpcore_componentrenderer__not-found", children: _jsx(ViewNotFound, { diagnostic: diagnostic, notFoundMessage: notFoundMessage }) }));
    }
    // Component-level permission gate — the live registry is the source of truth
    // (persisted dashboard JSON could carry stale role fields)
    const registeredDef = lookupComponent(resolvedInstance.id);
    if (registeredDef && !isUserAllowed(context, registeredDef.userGroups || [], registeredDef.appRoles || [])) {
        // Default (and `true`) shows the error; `false` opts into hiding the component instead.
        if (registeredDef.showUnauthorizedError === false)
            return null;
        return (_jsx("div", { className: "uxpcore_componentrenderer__container", children: _jsx(NotAuthorized, { mode: componentInstance != null ? 'inline' : 'default', description: componentInstance != null ? context.$L('uxp-core.text.not-authorized-widget') : undefined }) }));
    }
    // Addon check — show install/configure placeholder for uninstalled/unconfigured addon components
    const addonModuleId = getModuleIdFromWidget(resolvedInstance);
    const isFromAddOn = addOns.isAddOn(addonModuleId);
    if (isFromAddOn) {
        const { installed, configured } = addOns.isAddOnInstalledAndConfigured(addonModuleId);
        const needToConfigure = addOns.isConfigurationRequired(addonModuleId);
        const showAddonPlaceholder = !installed || (!configured && needToConfigure);
        if (showAddonPlaceholder) {
            const addOnName = addOns.getAddOnName(resolvedInstance, addonModuleId);
            return (_jsxs("div", { className: "uxpcore_componentrenderer__container", children: [_jsx(DefaultLoader, {}), _jsx("div", { className: "uxpcore_componentrenderer__addon-placeholder", children: _jsx("div", { className: "uxpcore_componentrenderer__addon-placeholder-content", style: { backgroundImage: `url(${addOns.getThunmbNailImagePath(addonModuleId)})` }, children: _jsxs("div", { className: "uxpcore_componentrenderer__addon-placeholder-details", children: [_jsx("p", { className: "uxpcore_componentrenderer__addon-placeholder-name", children: resolvedInstance.name }), _jsx("p", { className: "uxpcore_componentrenderer__addon-placeholder-addon-name", children: addOnName }), _jsx("p", { className: "uxpcore_componentrenderer__addon-placeholder-message", children: _jsx("span", { dangerouslySetInnerHTML: { __html: !installed
                                                    ? context.$L('uxp-core.addon.install-and-configure')
                                                    : context.$L('uxp-core.addon.configure')
                                            } }) }), _jsx("div", { className: "uxpcore_componentrenderer__addon-placeholder-actions", children: _jsx(ButtonComponent, { title: !installed ? context.$L('uxp-core.text.install') : context.$L('uxp-core.text.configure'), onClick: () => setInstallInstance(resolvedInstance), leftIcon: !installed ? 'fas cloud-download' : 'fas cog' }) })] }) }) }), _jsx(InstallAddOn, { widget: installInstance, show: installInstance !== null, onInstallationComplete: (w) => { onAddonInstalled === null || onAddonInstalled === void 0 ? void 0 : onAddonInstalled(w); setInstallInstance(null); }, onConfigurationComplete: (w) => { onAddonInstalled === null || onAddonInstalled === void 0 ? void 0 : onAddonInstalled(w); setInstallInstance(null); }, onClose: () => setInstallInstance(null) })] }));
        }
    }
    // Augment the instance with caller-supplied overrides for validation
    const schemaDefaults = (_a = resolvedInstance.defaultProps) !== null && _a !== void 0 ? _a : extractDefaultPropsFromSchema(resolvedInstance);
    const resolvedConfiguredProps = (_b = configuredProps !== null && configuredProps !== void 0 ? configuredProps : resolvedInstance.configuredProps) !== null && _b !== void 0 ? _b : {};
    const resolvedHasConfigured = (_c = hasConfigured !== null && hasConfigured !== void 0 ? hasConfigured : resolvedInstance.hasConfigured) !== null && _c !== void 0 ? _c : Object.keys(resolvedConfiguredProps).length > 0;
    const checkInstance = Object.assign(Object.assign({}, resolvedInstance), { defaultProps: schemaDefaults, configuredProps: resolvedConfiguredProps, hasConfigured: resolvedHasConfigured });
    if (shouldShowPlaceholder(checkInstance)) {
        return (_jsx("div", { className: "uxpcore_componentrenderer__container", children: _jsx(ConfigPlaceholder, { name: resolvedInstance.name, message: configNeededMessage, onConfigure: onConfigureRequest }) }));
    }
    if (forceConfigView) {
        if (hasConfigOptions(resolvedInstance)) {
            return (_jsx("div", { className: "uxpcore_componentrenderer__container", children: _jsx(ConfigPlaceholder, { name: resolvedInstance.name, message: configNeededMessage, onConfigure: onConfigureRequest }) }));
        }
        return (_jsx("div", { className: "uxpcore_componentrenderer__container uxpcore_componentrenderer__no-config", children: _jsx("p", { className: "uxpcore_componentrenderer__no-config-message", children: context.$L('uxp-core.text.no-configuration-required') }) }));
    }
    const comp = resolvedInstance.component;
    if (!comp || !isValidElementType(comp)) {
        // Still unresolved after the self-heal above (Path B): the same not-found state as a
        // failed lookup, with its diagnostic, instead of a bare "Invalid Component" line.
        const invalidModuleId = getModuleIdFromWidget(resolvedInstance);
        const hasBundle = ((_f = (_e = (_d = context === null || context === void 0 ? void 0 : context.scriptFiles) === null || _d === void 0 ? void 0 : _d[invalidModuleId]) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : 0) > 0;
        return (_jsx("div", { className: "uxpcore_componentrenderer__container uxpcore_componentrenderer__not-found", children: _jsx(ViewNotFound, { diagnostic: Object.assign({ cause: hasBundle ? 'not-registered' : 'bundle-missing', id: resolvedInstance.id }, describeModule(context, invalidModuleId)), notFoundMessage: notFoundMessage }) }));
    }
    // Strip React reserved props before spreading to avoid key/ref clashes
    const _g = (additionalProps || {}), { key: _k, ref: _r } = _g, safeAdditionalProps = __rest(_g, ["key", "ref"]);
    const merged = Object.assign(Object.assign(Object.assign({}, getMergedWidgetProps(checkInstance)), safeAdditionalProps), { uxpContext: context, isPreview });
    return (_jsx("div", { className: "uxpcore_componentrenderer__container", children: React.createElement(comp, merged) }));
};
/**
 * @export
 * A component to fetch and render any widget or UI component dynamically.
 *
 * @category Utilities
 */
export const ComponentRenderer = memo((props) => (_jsx(ErrorBoundary, { children: _jsx(ComponentRendererBase, Object.assign({}, props)) })));
export default ComponentRenderer;
